import { useMemo } from "react";

import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import type {
  FeatureRoadmapVerdictRow,
  FeatureRow,
} from "@/infrastructure/supabase/database.types";
import { DOMAINS, type Domain } from "@/presentation/data/domains";
import {
  ROADMAP_BRANCHES,
  verdictsForBranch,
  type RoadmapBranch,
  type Verdict,
} from "@/presentation/data/roadmaps";

export interface DomainStat {
  domain: Domain;
  total: number;
  byVerdict: Map<Verdict, number>;
  unverdicted: number;
}

export interface BranchStat {
  branch: RoadmapBranch;
  totalFeatures: number;
  totalVerdicted: number;
  domains: DomainStat[];
}

export function useRoadmapsOverview() {
  const featuresQuery = useFeaturesList();
  const verdictsQuery = useAllVerdicts();

  const features = featuresQuery.data ?? [];
  const verdicts = verdictsQuery.data ?? [];

  const branches = useMemo<BranchStat[]>(() => {
    return ROADMAP_BRANCHES.map((branch) => buildBranchStat(branch, features, verdicts));
  }, [features, verdicts]);

  return {
    isLoading: featuresQuery.isPending || verdictsQuery.isPending,
    error: featuresQuery.error ?? verdictsQuery.error ?? null,
    branches,
    totalFeatures: features.length,
  };
}

function buildBranchStat(
  branch: RoadmapBranch,
  features: readonly FeatureRow[],
  verdicts: readonly FeatureRoadmapVerdictRow[]
): BranchStat {
  const branchVerdicts = verdicts.filter((v) => v.branch === branch);
  const verdictsByFeature = new Map<string, FeatureRoadmapVerdictRow>();
  for (const v of branchVerdicts) verdictsByFeature.set(v.feature_id, v);

  const domains: DomainStat[] = DOMAINS.map((domain) => {
    const inDomain = features.filter((f) => f.domain === domain);
    const byVerdict = new Map<Verdict, number>();
    for (const v of verdictsForBranch(branch)) byVerdict.set(v, 0);
    let unverdicted = 0;
    for (const f of inDomain) {
      const v = verdictsByFeature.get(f.id);
      if (v) {
        byVerdict.set(v.verdict, (byVerdict.get(v.verdict) ?? 0) + 1);
      } else {
        unverdicted += 1;
      }
    }
    return { domain, total: inDomain.length, byVerdict, unverdicted };
  });

  return {
    branch,
    totalFeatures: features.length,
    totalVerdicted: verdictsByFeature.size,
    domains,
  };
}
